import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/config/axiosConfig";
import { Loader2 } from "lucide-react";
import useCurrencyStore from "@/stores/ui/currencyStore";
import { format } from "date-fns";
import waytoraLogo from "@/assets/logos/internal/waytora_blue_text_logo.svg";

// Move progressMessages outside component
const progressMessages = {
  accommodation: [
    "Preparing your stay...",
    "Checking room availability...",
    "Securing the best rate...",
    "Setting up your booking...",
  ],
  flight: [
    "Preparing your flight booking...",
    "Checking seat availability...",
    "Securing your fare...",
    "Almost there! Finalizing your reservation...",
  ],
  transport: [
    "Preparing your transport booking...",
    "Checking availability...",
    "Securing your fare...",
    "Almost there! Finalizing your reservation...",
  ],
  activity: ["Preparing your experience...", "Sending to Viator..."],
};

// Add TypeScript types
type BookingType = "accommodation" | "transport" | "activity";
type BookingParams = {
  tripId: string;
  type: BookingType;
  itemId: string;
};

interface BookingState {
  isLoading: boolean;
  error: string | null;
}

interface BookingComponentProps {
  name?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
  fromLocation?: string | null;
  toLocation?: string | null;
  currentMessageIndex: number;
  airlines?: string[] | null;
}

const AccommodationBooking: React.FC<BookingComponentProps> = ({
  name,
  checkIn,
  checkOut,
  currentMessageIndex,
}) => {
  return (
    <div className="flex flex-col items-center space-y-6">
      <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
        <i className="fa-solid fa-bed text-2xl text-indigo-600"></i>
      </div>

      <div className="text-center space-y-3">
        <h2 className="text-xl font-medium text-neutral-800">
          {name || "Accommodation Booking"}
        </h2>
        {checkIn && checkOut && (
          <p className="text-neutral-500 text-base">
            {format(new Date(checkIn), "MMM d")} -{" "}
            {format(new Date(checkOut), "MMM d, yyyy")}
          </p>
        )}
      </div>

      <div className="flex items-center justify-center space-x-3 bg-indigo-50 px-4 py-3 rounded-full w-full">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
        <p className="text-sm text-neutral-700 font-light">
          {progressMessages.accommodation[currentMessageIndex]}
        </p>
      </div>
    </div>
  );
};

const FlightBooking: React.FC<BookingComponentProps> = ({
  name,
  fromLocation,
  toLocation,
  currentMessageIndex,
  airlines,
}) => {
  const renderAirlineAvatars = () => {
    if (!airlines?.length) return null;

    return airlines.map((airline, index) => {
      const imageUrl = `https://images.kiwi.com/airlines/128x128/${airline}.png?default=airline.png`;
      return (
        <div
          key={airline}
          className="airline-avatar rounded-full border-2 border-white shadow-sm bg-white"
          style={{
            width: "2.5rem",
            height: "2.5rem",
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            marginLeft: index > 0 ? "-1rem" : "0",
            zIndex: airlines.length - index,
          }}
        />
      );
    });
  };

  return (
    <div className="flex flex-col items-center space-y-6">
      <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
        <i className="fa-solid fa-plane text-2xl text-indigo-600"></i>
      </div>

      <div className="text-center space-y-3">
        <h2 className="text-xl font-medium text-neutral-800">
          {name || "Flight Booking"}
        </h2>
        {fromLocation && toLocation && (
          <p className="text-neutral-500 text-base">
            {fromLocation} → {toLocation}
          </p>
        )}
        {airlines && airlines.length > 0 && (
          <div className="flex justify-center mt-2">
            <div className="flex">{renderAirlineAvatars()}</div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center space-x-3 bg-indigo-50 px-4 py-3 rounded-full w-full">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
        <p className="text-sm text-neutral-700 font-light">
          {progressMessages.flight[currentMessageIndex]}
        </p>
      </div>
    </div>
  );
};

const TransportBooking: React.FC<BookingComponentProps> = ({
  name,
  fromLocation,
  toLocation,
  currentMessageIndex,
}) => {
  return (
    <div className="flex flex-col items-center space-y-6">
      <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
        <i className="fa-solid fa-car text-2xl text-indigo-600"></i>
      </div>

      <div className="text-center space-y-3">
        <h2 className="text-xl font-medium text-neutral-800">
          {name || "Transport Booking"}
        </h2>
        {fromLocation && toLocation && (
          <p className="text-neutral-500 text-base">
            {fromLocation} → {toLocation}
          </p>
        )}
      </div>

      <div className="flex items-center justify-center space-x-3 bg-indigo-50 px-4 py-3 rounded-full w-full">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
        <p className="text-sm text-neutral-700 font-light">
          {progressMessages.transport[currentMessageIndex]}
        </p>
      </div>
    </div>
  );
};

const ExperienceBooking: React.FC<BookingComponentProps> = ({
  name,
  checkIn,
  currentMessageIndex,
}) => {
  return (
    <div className="flex flex-col items-center space-y-6">
      <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
        <i className="fa-solid fa-ticket text-2xl text-indigo-600"></i>
      </div>

      <div className="text-center space-y-3">
        <h2 className="text-xl font-medium text-neutral-800">
          {name || "Activity Booking"}
        </h2>
        {checkIn && (
          <p className="text-neutral-500 text-base">
            {format(new Date(checkIn), "MMM d, yyyy")}
          </p>
        )}
      </div>

      <div className="flex items-center justify-center space-x-3 bg-indigo-50 px-4 py-3 rounded-full w-full">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
        <p className="text-sm text-neutral-700 font-light">
          {progressMessages.activity[currentMessageIndex]}
        </p>
      </div>
    </div>
  );
};

const Booking: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tripId, type, itemId } = useParams<BookingParams>();
  const { selectedCurrency } = useCurrencyStore();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [bookingState, setBookingState] = useState<BookingState>({
    isLoading: true,
    error: null,
  });

  // Get metadata from URL params
  const name = searchParams.get("name");
  const checkIn = searchParams.get("checkIn");
  const checkOut = searchParams.get("checkOut");
  const fromLocation = searchParams.get("fromLocation");
  const toLocation = searchParams.get("toLocation");
  const airlines = searchParams.get("airlines")?.split(",");

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) =>
        prev <
        progressMessages[type as keyof typeof progressMessages].length - 1
          ? prev + 1
          : prev
      );
    }, 1500);

    return () => clearInterval(messageInterval);
  }, [type]);

  useEffect(() => {
    let isSubscribed = true;
    let redirectTimeout: NodeJS.Timeout;
    let errorRedirectTimeout: NodeJS.Timeout;

    const fetchBookingDetails = async () => {
      if (!itemId || !type || !tripId || !selectedCurrency) {
        setBookingState({
          isLoading: false,
          error: "Missing required booking information",
        });
        return;
      }

      try {
        const response = await api.book.get(`/${type}/details`, {
          params: {
            item_id: itemId,
            trip_id: tripId,
            currency: selectedCurrency.code,
          },
        });

        if (!isSubscribed) return;

        // Get booking URL based on type
        const bookingUrl = response.data.redirectUrl;

        if (
          !bookingUrl ||
          typeof bookingUrl !== "string" ||
          !bookingUrl.startsWith("http")
        ) {
          throw new Error("Invalid booking URL received");
        }

        if (type === "transport") {
          redirectTimeout = setTimeout(() => {
            window.location.href = bookingUrl;
          }, 1000);
        } else {
          window.location.href = bookingUrl;
        }
      } catch (err) {
        if (!isSubscribed) return;

        const errorMessage =
          err instanceof Error ? err.message : "Failed to process booking";
        setBookingState({ isLoading: false, error: errorMessage });

        // Redirect to error page after delay
        errorRedirectTimeout = setTimeout(() => {
          navigate(`/trip-builder/${tripId}`, {
            state: { error: errorMessage },
          });
        }, 3000);
      }
    };

    fetchBookingDetails();

    return () => {
      isSubscribed = false;
      if (redirectTimeout) clearTimeout(redirectTimeout);
      if (errorRedirectTimeout) clearTimeout(errorRedirectTimeout);
    };
  }, [itemId, type, tripId, selectedCurrency, navigate]);

  // Show error state
  if (bookingState.error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-50">
        <div className="bg-white shadow-lg ring-1 ring-red-200 p-8 rounded-xl max-w-xl w-full mx-4">
          <div className="text-center text-red-600">
            <h2 className="text-xl font-medium mb-2">Booking Error</h2>
            <p className="text-sm">{bookingState.error}</p>
            <p className="text-sm mt-2">Redirecting you back...</p>
          </div>
        </div>
      </div>
    );
  }

  const renderBookingComponent = () => {
    const commonProps = {
      name,
      checkIn,
      checkOut,
      fromLocation,
      toLocation,
      currentMessageIndex,
      airlines,
    };

    switch (type) {
      case "accommodation":
        return <AccommodationBooking {...commonProps} />;
      case "transport":
        return <FlightBooking {...commonProps} />;
      case "activity":
        return <ExperienceBooking {...commonProps} />;
      default:
        return <AccommodationBooking {...commonProps} />;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-50">
      <img
        src={waytoraLogo}
        alt="Waytora"
        className="absolute h-12 mb-8 top-2 left-1/2 transform -translate-x-1/2"
      />
      <div className="bg-white shadow-lg ring-1 ring-indigo-200 p-8 rounded-xl max-w-xl w-full mx-4">
        {renderBookingComponent()}
      </div>
    </div>
  );
};

export default Booking;
